import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { Breadcrumbs } from '@fuww/library/src/Breadcrumb';
import CourseComponent from '@fuww/library/src/Course';
import Anchor from '@fuww/library/src/Anchor';
import Head from '../Head';
import HomeLink from '../HomeLink';
import withQuery from '../../lib/withQuery';
import shareList from '../../lib/shareData';
import LinkAndAnchor from '../LinkAndAnchor';
import loading from '../../lib/loading';
import { useSiteContext } from '../SiteContext';
import throw404 from '../../lib/throw404';
import ImagePreloader from '../ImagePreloader';
import getSourceSet from '../../lib/getSourceSet';

const DynamicShareButton = dynamic(() => import(
  '@fuww/library/src/SocialShare/index'
), {
  ssr: false,
  loading,
});

const Course = ({
  data: { course }, variables: {
    imageConfigurations,
  },
}) => {
  const { url, locale } = useSiteContext();

  if (!course) return throw404();

  const {
    id, title, description, city, country, companyName,
    additionalInformation, education, slug, introduction,
    logoUrls,
  } = course;

  const filteredShareList = shareList({
    title, url, locale,
  })
    .filter((share) => !share.checkMedia);

  const breadcrumbs = [
    <HomeLink />,
    <Anchor styled href="https://fashionunited.com/education">
      Education
    </Anchor>,
    <LinkAndAnchor
      route="courses"
    >
      Courses
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="course"
      params={{ id, slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={title}
        description={introduction}
        subjectType="Course"
        subjectId={id}
      />
      <ImagePreloader
        src={logoUrls[1]}
        placeholder={logoUrls[0]}
        sizes="250px"
        srcSet={getSourceSet(imageConfigurations, logoUrls)}
        placeholderFetchpriority="high"
        finalFetchpriority="high"
      />
      <CourseComponent
        breadcrumbs={(
          <Breadcrumbs
            crumbs={breadcrumbs}
          />
        )}
        title={title}
        companyName={companyName}
        location={`${country}, ${city}`}
        education={education}
        description={description}
        additionalInformation={additionalInformation}
        shareButtons={(
          <DynamicShareButton
            data={filteredShareList}
          />
        )}
      />
    </>
  );
};

Course.propTypes = {
  data: PropTypes.shape({
    course: PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      companyName: PropTypes.string,
      additionalInformation: PropTypes.string,
      education: PropTypes.string,
      slug: PropTypes.string,
      introduction: PropTypes.string,
      logoUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  }),
  variables: PropTypes.shape({
    imageConfigurations: PropTypes.arrayOf(
      PropTypes.shape({}),
    ).isRequired,
  }).isRequired,
};
Course.defaultProps = {
  data: {},
};

export default withQuery(Course);
